import { isEmptyCompositionBucket } from '@revelio/core/composition-buckets';

import {
  type CompositionV3AnyGraphqlResponse,
  type CompositionV3FullMetrics,
  isCompositionV3FullMetrics,
} from '../../../../DataProvider/composition/composition-v3/composition-v3-graphql-response';
import {
  type BreakdownSingleDataSet,
  DataType,
} from '../../../../DataProvider/types';
import type { CompositionSecondaryDimensionValues } from '../../../../PageConfigProvider/types/dimension';
import { convertBreakdownSingleDataToSheet } from './breakdown-single-data-converter';

interface ShareOfSegmentData {
  compositionData: CompositionV3AnyGraphqlResponse;
  secondaryDimension: CompositionSecondaryDimensionValues;
  selectedSegmentIds: string[];
  dateRefs: string[];
}

type SegmentMetric = {
  headcount: { id: number; count?: number | null; share?: number | null }[];
};

type EntityData = {
  entityName: string;
  selectedMetrics: SegmentMetric[];
};

const fullMetricsForEntity = (
  entity: NonNullable<CompositionV3AnyGraphqlResponse['entities']>[number]
): CompositionV3FullMetrics | undefined =>
  isCompositionV3FullMetrics(entity.metrics) ? entity.metrics : undefined;

export const convertShareOfSegmentData = ({
  data,
  primaryDimension,
  secondaryDimension,
  metric,
}: {
  data: ShareOfSegmentData;
  primaryDimension: string;
  secondaryDimension: string;
  metric: string;
}) => {
  const { compositionData, selectedSegmentIds, dateRefs } = data;

  const entityDataByDate = (compositionData.entities ?? []).reduce<
    EntityData[]
  >((acc, entity) => {
    const entityName = entity.short_name;
    const metricData = fullMetricsForEntity(entity)?.[data.secondaryDimension];

    if (!metricData || !entityName) return acc;

    const selectedMetrics = metricData
      .filter((segment) => !isEmptyCompositionBucket(segment.id))
      .filter((segment) => selectedSegmentIds.includes(`${segment.id}`));

    if (selectedMetrics.length === 0) return acc;

    const hasTimeseriesData = selectedMetrics.some(
      (segment) => (segment.headcount?.length ?? 0) > 0
    );
    if (!hasTimeseriesData) {
      return acc;
    }

    return [
      ...acc,
      {
        entityName,
        selectedMetrics,
      },
    ];
  }, []);

  const dataByDate = entityDataByDate.reduce<
    Record<string, Record<string, { count: number; share: number }>>
  >((dateAcc, { entityName, selectedMetrics }) => {
    for (const segment of selectedMetrics) {
      for (const timePoint of segment.headcount) {
        const date = dateRefs[timePoint.id];
        if (!date) continue;

        const dateEntry = (dateAcc[date] ??= {});
        const entityEntry = (dateEntry[entityName] ??= { count: 0, share: 0 });
        entityEntry.count += timePoint.count ?? 0;
        entityEntry.share += (timePoint.share ?? 0) / 100;
      }
    }
    return dateAcc;
  }, {});

  const sortedEntityNames = [
    ...new Set(entityDataByDate.map((d) => d.entityName)),
  ].sort();
  const sortedDates = Object.keys(dataByDate).sort();

  const breakdownData = sortedEntityNames.map((entityName) => ({
    dim: entityName,
    breakdown: sortedDates.map((date) => ({
      dim: date,
      value: dataByDate[date]?.[entityName]?.count ?? 0,
      share: dataByDate[date]?.[entityName]?.share ?? 0,
    })),
  }));

  const breakdownSingleDataSet: BreakdownSingleDataSet = {
    type: DataType.BREAKDOWN_SINGLE,
    data: breakdownData,
  };

  return convertBreakdownSingleDataToSheet({
    data: breakdownSingleDataSet,
    primaryDimension,
    secondaryDimension,
    metric,
  });
};
